import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { useStudentStore } from '../store/StudentStore';
import ProgrammeService from '../services/ProgrammeService';
import StaffService from '../services/StaffService';
import { formatDate } from '../utils/dateFormat';

export default function StaffDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const programmeService = new ProgrammeService();
  const staffService = new StaffService();
  const programmes = programmeService.list();
  const staff = staffService.getById(id);
  const { inventoryController, incidents } = useStudentStore();

  const assignedProgrammes = (() => {
    if (!staff) return [];
    const staffProgrammes = staff.programmes || staff.programmeIds || [];
    return programmes.filter(
      (programme) => staffProgrammes.includes(programme.id) || staffProgrammes.includes(programme.name)
    );
  })();

  const certificates = staff?.certificates?.length ? staff.certificates : staff?.documents || [];

  const leaveEntries = (() => {
    if (!staff) return [];
    const sick = (staff.sickEntries || []).map((entry) => ({ ...entry, type: 'Sick' }));
    const holiday = (staff.holidayEntries || []).map((entry) => ({ ...entry, type: 'Holiday' }));
    return [...sick, ...holiday].sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
  })();

  const inventoryAssignments = (() => {
    if (!staff) return [];
    return inventoryController.getAssignmentsForStaff(staff.id);
  })();

  const incidentInvolvement = (() => {
    if (!staff) return [];
    return incidents.filter((incident) =>
      (incident.participants || []).some((participant) => participant.type === 'staff' && participant.id === staff.id)
    );
  })();

  const toggleArchive = () => {
    if (!staff) return;
    const nextStatus = staff.status === 'archived' ? 'active' : 'archived';
    staffService.update({ ...staff, status: nextStatus });
    navigate('/support-workers');
  };

  if (!staff) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Staff member not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/support-workers')}>
          Back to Support Workers
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>{staff.name}</h2>
          <p className="sp-muted">{staff.role} · {staff.status}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => navigate('/support-workers')}>
            Back to list
          </Button>
          <Button variant="secondary" size="sm" onClick={() => navigate(`/support-workers/${staff.id}/edit`)}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={toggleArchive}>
            {staff.status === 'archived' ? 'Restore' : 'Archive'}
          </Button>
        </div>
      </div>

      <Card className="p-5">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <div className="sp-label">Email</div>
            <div className="sp-value" style={{ fontWeight: 600 }}>{staff.email || '—'}</div>
          </div>
          <div>
            <div className="sp-label">Phone</div>
            <div className="sp-value" style={{ fontWeight: 600 }}>{staff.phone || '—'}</div>
          </div>
          <div>
            <div className="sp-label">Employment</div>
            <div className="sp-value" style={{ fontWeight: 600 }}>
              {formatDate(staff.startDate) || '—'} {staff.endDate ? `→ ${formatDate(staff.endDate)}` : ''}
            </div>
          </div>
          <div className="md:col-span-3">
            <div className="sp-label">Address</div>
            <div className="sp-value" style={{ fontWeight: 600 }}>
              {(staff.address || []).length ? staff.address.join(', ') : '—'}
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Programme Assignments</h3>
            <p className="text-sm sp-muted">Programmes and activities assigned to this staff member.</p>
          </div>
        </div>
        <div className="sp-table sp-table--alerts">
          <div className="sp-row sp-row--header">
            <div>Programme</div>
            <div>Day</div>
            <div>Time</div>
            <div>Capacity</div>
          </div>
          {assignedProgrammes.map((programme) => (
            <div key={programme.id} className="sp-row">
              <div className="sp-value" style={{ fontWeight: 600 }}>{programme.name}</div>
              <div className="text-sm sp-muted">{programme.day || programme.days?.join(', ') || 'TBD'}</div>
              <div className="text-sm sp-muted">{programme.time || 'TBD'}</div>
              <div className="text-sm sp-muted">{programme.capacity || '—'}</div>
            </div>
          ))}
          {assignedProgrammes.length === 0 && (
            <div className="sp-row">
              <div style={{ gridColumn: '1 / -1' }} className="text-sm sp-muted">
                No assigned programmes.
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Certificates & Compliance</h3>
            <p className="text-sm sp-muted">Expiry dates and uploaded certificates.</p>
          </div>
        </div>
        <div className="sp-table sp-table--alerts">
          <div className="sp-row sp-row--header">
            <div>Certificate</div>
            <div>Expiry</div>
            <div>Status</div>
            <div>Files</div>
          </div>
          {certificates.map((cert) => {
            const expiryDate = cert.expiryDate || cert.expiry;
            return (
              <div key={cert.id || cert.name} className="sp-row">
                <div className="sp-value" style={{ fontWeight: 600 }}>{cert.name || 'Certificate'}</div>
                <div className="text-sm sp-muted">{expiryDate || '—'}</div>
                <div>
                  <Badge variant={expiryDate ? 'review' : 'inactive'}>
                    {expiryDate ? 'Tracked' : 'No expiry'}
                  </Badge>
                </div>
                <div className="text-sm sp-muted">
                  {(cert.files || []).length ? cert.files.join(', ') : '—'}
                </div>
              </div>
            );
          })}
          {certificates.length === 0 && (
            <div className="sp-row">
              <div style={{ gridColumn: '1 / -1' }} className="text-sm sp-muted">
                No certificates uploaded.
              </div>
            </div>
          )}
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Leave Records</h3>
              <p className="text-sm sp-muted">Sick and holiday entries.</p>
            </div>
          </div>
          <div className="sp-table sp-table--alerts">
            <div className="sp-row sp-row--header">
              <div>Type</div>
              <div>Start</div>
              <div>End</div>
              <div>Notes</div>
            </div>
            {leaveEntries.map((entry) => (
              <div key={entry.id} className="sp-row">
                <div className="sp-value" style={{ fontWeight: 600 }}>{entry.type}</div>
                <div className="text-sm sp-muted">{formatDate(entry.startDate) || '—'}</div>
                <div className="text-sm sp-muted">{formatDate(entry.endDate) || '—'}</div>
                <div className="text-sm sp-muted">{entry.notes || '—'}</div>
              </div>
            ))}
            {leaveEntries.length === 0 && (
              <div className="sp-row">
                <div style={{ gridColumn: '1 / -1' }} className="text-sm sp-muted">
                  No leave entries recorded.
                </div>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Inventory Assignments</h3>
              <p className="text-sm sp-muted">Items currently assigned to this staff member.</p>
            </div>
          </div>
          <div className="sp-table sp-table--alerts">
            <div className="sp-row sp-row--header">
              <div>Item</div>
              <div>Status</div>
              <div>Due</div>
              <div>Condition</div>
            </div>
            {inventoryAssignments.map(({ item, assignment, due }) => (
              <div key={assignment.id} className="sp-row">
                <div className="sp-value" style={{ fontWeight: 600 }}>{item.name}</div>
                <div>
                  <Badge variant={due.isOverdue ? 'danger' : 'active'}>
                    {due.isOverdue ? 'Overdue' : 'Assigned'}
                  </Badge>
                </div>
                <div className="text-sm sp-muted">{formatDate(assignment.dueDate) || '—'}</div>
                <div className="text-sm sp-muted">{item.condition}</div>
              </div>
            ))}
            {inventoryAssignments.length === 0 && (
              <div className="sp-row">
                <div style={{ gridColumn: '1 / -1' }} className="text-sm sp-muted">
                  No inventory assigned.
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Incident Involvement</h3>
            <p className="text-sm sp-muted">Incidents that include this staff member.</p>
          </div>
        </div>
        <div className="sp-table sp-table--incidents">
          <div className="sp-row sp-row--header">
            <div>Date</div>
            <div>Type</div>
            <div>Severity</div>
            <div>Students</div>
            <div>Status</div>
            <div>Follow-up</div>
          </div>
          {incidentInvolvement.map((incident) => {
            const studentNames = incident.participants
              .filter((p) => p.type === 'student')
              .map((p) => p.name || p.id)
              .join(', ');
            return (
              <div key={incident.id} className="sp-row">
                <div className="text-sm sp-muted">{formatDate(incident.date)}</div>
                <div className="sp-value" style={{ fontWeight: 600 }}>{incident.type}</div>
                <div>
                  <Badge variant={incident.severity}>{incident.severity}</Badge>
                </div>
                <div className="text-sm sp-muted">{studentNames || '—'}</div>
                <div>
                  <Badge variant={incident.status === 'open' ? 'review' : incident.status === 'monitoring' ? 'info' : 'inactive'}>
                    {incident.status}
                  </Badge>
                </div>
                <div className="text-sm sp-muted">
                  {incident.followUpRequired ? formatDate(incident.followUpDate) || 'Required' : 'None'}
                </div>
              </div>
            );
          })}
          {incidentInvolvement.length === 0 && (
            <div className="sp-row">
              <div style={{ gridColumn: '1 / -1' }} className="text-sm sp-muted">
                No incidents linked to this staff member.
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
